import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Eye, EyeOff, RefreshCw, Mail, CheckCircle2, ShieldCheck, Cpu, Sparkles, Shield, Radar } from 'lucide-react';
import Logo from '../components/Logo';

const productShowcase = [
  { icon: Cpu, labelKey: 'topbar.reverse_model_name', descKey: 'topbar.reverse_model_desc', color: 'text-emerald-400', bg: 'bg-emerald-500/10', borderColor: 'border-emerald-500/20' },
  { icon: Sparkles, labelKey: 'topbar.finetuning', descKey: 'topbar.finetuning_desc', color: 'text-amber-400', bg: 'bg-amber-500/10', borderColor: 'border-amber-500/20' },
  { icon: Shield, labelKey: 'topbar.malware_analysis', descKey: 'topbar.malware_analysis_desc', color: 'text-blue-400', bg: 'bg-blue-500/10', borderColor: 'border-blue-500/20' },
  { icon: Radar, labelKey: 'topbar.vuln_mining', descKey: 'topbar.vuln_mining_desc', color: 'text-indigo-400', bg: 'bg-indigo-500/10', borderColor: 'border-indigo-500/20' },
];
import BackButton from '../components/BackButton';
import { useAuth } from '../contexts/AuthContext';

interface CaptchaData {
  id: string;
  question: string;
  svg?: string;
}

// Get the API base URL for fetch calls
// In production with split-origin deployment, use VITE_API_URL
// In development, use relative path (Vite dev proxy handles routing)
const getApiUrl = (path: string): string => {
  if (import.meta.env.VITE_API_URL && import.meta.env.PROD) {
    return `${import.meta.env.VITE_API_URL}${path}`;
  }
  return path;
};

// Password strength indicator
const PasswordStrength = ({ password }: { password: string }) => {
  const { t } = useTranslation('auth');
  const checks = [
    { label: t('rules.min_chars'), met: password.length >= 8 },
    { label: t('rules.lowercase'), met: /[a-z]/.test(password) },
    { label: t('rules.uppercase'), met: /[A-Z]/.test(password) },
    { label: t('rules.number'), met: /[0-9]/.test(password) },
    { label: t('rules.symbol'), met: /[^A-Za-z0-9]/.test(password) },
  ];

  const metCount = checks.filter((c) => c.met).length;
  const isEligible = metCount >= 3;

  return (
    <div className={`mt-3 rounded-xl border p-3 transition-colors ${
      isEligible ? 'border-emerald-500/25 bg-emerald-500/5' : 'border-border bg-muted/30'
    }`}>
      <div className="flex items-center justify-between gap-3">
        <div className={`flex items-center gap-1.5 text-xs font-medium ${isEligible ? 'text-emerald-600' : 'text-muted-foreground'}`}>
          {isEligible && <ShieldCheck className="h-3.5 w-3.5" />}
          <span>{isEligible ? t('password_eligible') : t('password_not_eligible', { count: 3 - metCount })}</span>
        </div>
        <span className="shrink-0 rounded-full border border-border bg-background px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
          {metCount}/5
        </span>
      </div>
      <div className="mt-2 grid grid-cols-5 gap-1">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className={`h-1.5 rounded-full transition-colors ${i < metCount ? 'bg-emerald-500' : 'bg-border'}`} />
        ))}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-1.5">
        {checks.map((check) => (
          <div
            key={check.label}
            className={`flex min-h-8 min-w-0 items-center gap-1.5 rounded-lg border px-2 text-xs transition-colors ${
              check.met
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600'
                : 'border-border bg-background/70 text-muted-foreground'
            }`}
          >
            {check.met ? (
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
            ) : (
              <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-current opacity-40" />
            )}
            <span className="truncate">{check.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

import PageTitle from '../components/PageTitle';

export default function Auth() {
  const { t } = useTranslation(['auth', 'common']);
  const tc = (key: string) => t(key, { ns: 'common' });
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState<CaptchaData | null>(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [showTwoFactorVerification, setShowTwoFactorVerification] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorChallengeToken, setTwoFactorChallengeToken] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [rememberMe, setRememberMe] = useState(false);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  const passwordMeetsRules = (password: string) => {
    if (!password) return false;
    let score = 0;
    if (password.length >= 8) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    return score >= 3;
  };

  const { login, register, verifyEmail } = useAuth();
  const navigate = useNavigate();

  const redirectAfterAuth = (role?: string) => {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get('redirect');
    if (redirect) {
      try {
        const url = new URL(redirect, window.location.origin);
        if (url.origin === window.location.origin) {
          window.location.replace(url.toString());
          return;
        }
      } catch {
        // fall through to default navigation
      }
    }
    navigate(role === 'admin' ? '/dashboard' : '/console');
  };

  useEffect(() => {
    loadCaptcha();
  }, []);

  const loadCaptcha = async () => {
    try {
      const response = await fetch(getApiUrl('/api/auth/captcha'));
      if (response.ok) {
        const data = await response.json();
        setCaptcha(data);
        setCaptchaAnswer('');
      }
    } catch (error) {
      console.error('Failed to load captcha:', error);
    }
  };

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendCooldown]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!showEmailVerification) return;
      if (event.key === 'Escape') {
        setShowEmailVerification(false);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (emailVerificationCode.trim() && !loading) {
          handleEmailVerification();
        }
      }
    };
    if (showEmailVerification) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showEmailVerification, emailVerificationCode, loading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});

    if (!captcha || !captchaAnswer.trim()) {
      setFormErrors({ captcha: t('please_solve_puzzle') });
      return;
    }

    if (!isLogin && !passwordMeetsRules(formData.password)) {
      setFormErrors({ general: t('password_not_meet') });
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(
          formData.email,
          formData.password,
          captcha.id,
          captchaAnswer.trim(),
          rememberMe
        );
        if (result.needsTwoFactor) {
          setTwoFactorChallengeToken(result.challengeToken!);
          setShowTwoFactorVerification(true);
          setTwoFactorCode('');
          setCaptchaAnswer('');
          return;
        }
        if (result.needsEmailVerification) {
          setShowEmailVerification(true);
          setResendCooldown(60);
          return;
        }
        redirectAfterAuth(result.user?.role);
      } else {
        await register(
          formData.email,
          formData.password,
          captcha.id,
          captchaAnswer.trim()
        );
        setShowEmailVerification(true);
        setResendCooldown(60);
      }
    } catch (error: any) {
      loadCaptcha();
      setFormErrors({
        general: error?.response?.data?.error || error?.message || t('an_error_occurred'),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailVerification = async () => {
    setFormErrors({});
    if (!emailVerificationCode.trim()) {
      setFormErrors({ verification: t('enter_code') });
      return;
    }

    setLoading(true);
    try {
      const result = await verifyEmail(formData.email, emailVerificationCode);
      toast.success(t('email_verified'));
      redirectAfterAuth(result.user?.role);
    } catch (error: any) {
      const msg = error?.response?.data?.error || error?.message || t('verification_failed');
      setFormErrors({ verification: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setEmailVerificationCode('');
    setFormErrors({});
    setLoading(true);

    try {
      const response = await fetch(getApiUrl('/api/auth/resend-verification'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email }),
      });
      if (response.ok) {
        setResendCooldown(60);
        toast.success(t('code_sent'));
      } else {
        const errorData = await response.json().catch(() => ({}));
        setFormErrors({ resend: errorData.error || t('failed_resend') });
      }
    } catch (error: any) {
      setFormErrors({ resend: error?.message || t('network_error') });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleTwoFactorLogin = async () => {
    if (!twoFactorCode.trim()) {
      setFormErrors({ twoFactor: t('two_factor_required') });
      return;
    }

    setLoading(true);
    setFormErrors({});

    try {
      const result = await login(
        formData.email,
        formData.password,
        undefined,
        undefined,
        rememberMe,
        twoFactorCode.trim(),
        twoFactorChallengeToken,
      );

      if (result.needsTwoFactor) {
        setFormErrors({ twoFactor: t('two_factor_required') });
        setTwoFactorChallengeToken(result.challengeToken!);
        return;
      }

      setShowTwoFactorVerification(false);
      setTwoFactorCode('');
      setTwoFactorChallengeToken('');
      redirectAfterAuth(result.user?.role);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || error?.message || t('an_error_occurred');
      const nextErrors: Record<string, string> = {};
      if (String(errorMessage).toUpperCase().includes('2FA')) {
        nextErrors.twoFactor = t('two_factor_invalid');
      } else {
        nextErrors.twoFactor = errorMessage;
      }
      setFormErrors(nextErrors);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setIsLogin(!isLogin);
    setFormErrors({});
    setFormData({ email: '', password: '' });
    setCaptchaAnswer('');
    setTwoFactorCode('');
    setTwoFactorChallengeToken('');
    setShowTwoFactorVerification(false);
    loadCaptcha();
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground selection:bg-primary/30 relative overflow-hidden">
      <PageTitle 
        titleKey="page_title" 
        namespace="auth" 
        descriptionKey="hero_description"
      />
      {/* Subtle grid background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none"></div>

      {/* Back to Home */}
      <BackButton onClick={() => navigate('/')} label={t('back_to_home')} className="absolute top-5 left-5 z-50" />

      {/* Left panel — branding (desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center p-8 bg-muted/30 dark:bg-black/20 border-r border-border/50">
        <div className="relative z-10 w-full max-w-lg animate-slide-up flex flex-col gap-5">
          {/* Hero card */}
          <div className="rounded-2xl border border-border bg-card p-8 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -mr-20 -mt-20 opacity-50 transition-opacity group-hover:opacity-100"></div>
            
            <div className="flex items-center gap-3 mb-6 relative z-10">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Logo size="w-6 h-6" className="text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight">
                  {import.meta.env.VITE_PRODUCT_NAME || 'Etherworks'}
                </h1>
                <p className="text-sm text-muted-foreground">{t('hero_title')}</p>
              </div>
            </div>

            <h2 className="text-3xl font-bold leading-tight mb-3 tracking-tighter relative z-10">
              {t('hero_subtitle_1')}
              <span className="text-muted-foreground">{t('hero_subtitle_2')}</span>
            </h2>

            <p className="text-muted-foreground text-base leading-relaxed mb-6">
              {t('hero_description')}
            </p>

            <div className="flex flex-wrap gap-2">
              {(['features.weight_inspection', 'features.architecture_analysis', 'features.behavioral_probing', 'features.security_auditing'] as const).map((key) => (
                <span
                  key={key}
                  className="px-3 py-1.5 rounded-full text-sm font-medium bg-muted/60 text-muted-foreground"
                >
                  {t(key)}
                </span>
              ))}
            </div>
          </div>

          {/* Products & Models card */}
          <div className="rounded-2xl border border-border bg-card p-6 relative overflow-hidden">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
              {tc('topbar.products')} & {t('hero_title', { defaultValue: 'Models' })}
            </h3>
            <div className="grid gap-3">
              {productShowcase.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.labelKey}
                    className={`flex items-center gap-3 rounded-xl border ${item.borderColor} ${item.bg} px-4 py-3 transition-all duration-200 hover:scale-[1.02]`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${item.bg}`}>
                      <Icon className={`w-5 h-5 ${item.color}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{tc(item.labelKey)}</p>
                      <p className="text-xs text-muted-foreground truncate">{tc(item.descKey)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-md animate-slide-up" style={{ animationDelay: '0.1s', animationFillMode: 'both' }}>
          {/* Mobile-only logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Logo size="w-6 h-6" className="text-primary" />
            </div>
            <span className="text-xl font-bold tracking-tight">
              {import.meta.env.VITE_PRODUCT_NAME || 'Etherworks'}
            </span>
          </div>

          {/* Heading */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold tracking-tighter">
              {isLogin ? t('title') : t('title_register')}
            </h2>
            <p className="text-sm text-muted-foreground mt-2">
              {isLogin ? t('subtitle') : t('subtitle_register')}
            </p>
          </div>

          {/* Form card — solid background */}
          <div className="rounded-2xl border border-border bg-card p-8 shadow-xl">
          {/* Error message */}
          {formErrors.general && (
            <div className="mb-5 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
              <p className="text-sm text-destructive text-center font-medium">{formErrors.general}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div className="space-y-1.5">
              <label htmlFor="email" className="block text-sm font-medium">
                {t('email_address')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder={t('email_placeholder')}
                required
                autoComplete="email"
                className="input placeholder:text-muted-foreground/50"
              />
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label htmlFor="password" className="block text-sm font-medium">
                {t('password')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder={t('common:password_mask')}
                  required
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  className="input pr-10 placeholder:text-muted-foreground/50"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {!isLogin && formData.password && (
                <PasswordStrength password={formData.password} />
              )}
            </div>

            {/* Remember me */}
            {isLogin && (
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-border accent-primary cursor-pointer"
                />
                <span className="text-sm text-muted-foreground">{t('remember_me')}</span>
              </label>
            )}

            {/* Captcha */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
                  <label className="text-sm font-medium">
                    {t('security_verification')}
                  </label>
                </div>
                <button
                  type="button"
                  onClick={loadCaptcha}
                  className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                >
                  <RefreshCw className="h-3 w-3" />
                  <span>{t('new_puzzle')}</span>
                </button>
              </div>
              {captcha && (
                <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg overflow-hidden border border-border/50">
                  <div className="shrink-0 w-[135px] flex items-center justify-center bg-background border border-border rounded-lg overflow-hidden h-10">
                    {captcha.svg ? (
                      <div dangerouslySetInnerHTML={{ __html: captcha.svg }} className="w-full h-full flex justify-center items-center [&>svg]:w-full [&>svg]:h-full [&>svg]:object-cover [&>svg]:scale-110" />
                    ) : (
                      <span className="text-sm font-bold tracking-widest select-none" style={{ fontFamily: 'monospace', letterSpacing: '0.15em' }}>
                        {captcha.question.replace('Type the characters: ', '')}
                      </span>
                    )}
                  </div>
                  <input
                    type="text"
                    value={captchaAnswer}
                    onChange={(e) => setCaptchaAnswer(e.target.value)}
                    className="input min-w-0 flex-1 text-center font-bold placeholder:text-muted-foreground/50"
                    placeholder={t('common:code_mask')}
                    maxLength={6}
                  />
                </div>
              )}
              {formErrors.captcha && (
                <p className="text-sm text-destructive">{formErrors.captcha}</p>
              )}
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading || !captcha || !captchaAnswer.trim()}
              className="w-full flex items-center justify-center gap-2 h-11 px-8 rounded-md bg-primary text-primary-foreground font-medium transition-all hover:bg-primary/90 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  <span>{t('please_wait')}</span>
                </>
              ) : (
                <span>{isLogin ? t('sign_in') : t('create_account')}</span>
              )}
            </button>

          {isLogin && (
            <div className="flex justify-end">
              <Link to="/reset" className="text-sm font-medium text-primary hover:underline">
                {t('forgot_password', { defaultValue: 'Forgot password?' })}
              </Link>
            </div>
          )}
          </form>
        </div>

        {/* Switch mode — inline link */}
        <p className="mt-5 text-center text-sm text-muted-foreground">
          {isLogin ? t('new_here') : t('already_have_account')}{' '}
          <button
            onClick={switchMode}
            className="text-primary hover:underline font-medium"
          >
            {isLogin ? t('create_an_account') : t('sign_in_instead')}
          </button>
        </p>

        {/* Footer */}
        <div className="mt-6 flex items-center justify-center gap-4 text-xs text-muted-foreground">
          <a href="/docs/policies/terms" className="hover:text-foreground transition-colors">
            {t('terms')}
          </a>
          <span className="text-border">·</span>
          <a href="/docs/policies/privacy" className="hover:text-foreground transition-colors">
            {t('privacy')}
          </a>
        </div>
        </div>
      </div>

      {/* Email Verification Modal */}
      {showEmailVerification && (
        <div
          className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in"
          onKeyDown={e => e.key === 'Escape' && setShowEmailVerification(false)}
          tabIndex={-1}
        >
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-8 shadow-2xl animate-slide-up">
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-primary/10 flex items-center justify-center">
                <Mail className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-2xl font-bold mb-1 tracking-tighter">
                {t('verify_title')}
              </h3>
              <p className="text-sm text-muted-foreground mt-2">
                {t('verify_subtitle')}
                <br />
                <span className="text-foreground font-medium">{formData.email}</span>
              </p>
            </div>

            <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); void handleEmailVerification(); }}>
              <input
                type="text"
                value={emailVerificationCode}
                onChange={(e) =>
                  setEmailVerificationCode(
                    e.target.value.replace(/\D/g, '').slice(0, 6)
                  )
                }
                className="input h-auto px-4 py-3 text-center text-2xl tracking-[0.5em] font-bold placeholder:text-muted-foreground/50"
                placeholder="000000"
                maxLength={6}
                autoFocus
              />
              {formErrors.verification && (
                <p className="text-sm text-destructive text-center font-medium">
                  {formErrors.verification}
                </p>
              )}

              <button
                type="submit"
                disabled={loading || emailVerificationCode.length !== 6}
                className="w-full flex items-center justify-center gap-2 h-11 px-8 rounded-md bg-primary text-primary-foreground font-medium transition-all hover:bg-primary/90 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {loading ? t('verifying') : t('verify_button')}
              </button>

              <div className="flex items-center justify-between text-sm pt-1">
                <button
                  type="button"
                  onClick={() => setShowEmailVerification(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t('common:go_back')}
                </button>
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={loading || resendCooldown > 0}
                  className="text-primary hover:underline disabled:text-muted-foreground disabled:no-underline disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {resendCooldown > 0 ? t('resend_in', { seconds: resendCooldown }) : t('resend_code')}
                </button>
              </div>
              {formErrors.resend && (
                <p className="text-sm text-destructive text-center font-medium">
                  {formErrors.resend}
                </p>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Two-Factor Verification Modal */}
      {showTwoFactorVerification && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-8 shadow-2xl animate-slide-up">
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-primary/10 flex items-center justify-center">
                <ShieldCheck className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-2xl font-bold mb-1 tracking-tighter">
                {t('two_factor_title')}
              </h3>
              <p className="text-sm text-muted-foreground mt-2">
                {t('two_factor_subtitle')}
              </p>
            </div>

            <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); void handleTwoFactorLogin(); }}>
              <div className="space-y-1.5">
                <label className="block text-sm font-medium">{t('two_factor_code_label')}</label>
                <input
                  type="text"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="input h-auto px-4 py-3 text-center text-lg tracking-[0.2em] font-bold placeholder:text-muted-foreground/50"
                  placeholder={t('two_factor_placeholder')}
                  maxLength={6}
                  autoFocus
                />
                {formErrors.twoFactor && (
                  <p className="text-sm text-destructive text-center font-medium">
                    {formErrors.twoFactor}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !twoFactorCode.trim()}
                className="w-full flex items-center justify-center gap-2 h-11 px-8 rounded-md bg-primary text-primary-foreground font-medium transition-all hover:bg-primary/90 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {loading ? t('verifying') : t('two_factor_verify_button')}
              </button>

              <div className="flex items-center justify-between text-sm pt-1">
                <button
                  type="button"
                  onClick={async () => {
                    setShowTwoFactorVerification(false);
                    setTwoFactorCode('');
                    setTwoFactorChallengeToken('');
                    setCaptchaAnswer('');
                    await loadCaptcha();
                  }}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t('common:go_back')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
